import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  IconButton,
  Alert,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { withTranslation } from 'react-i18next';

import useCalendarState from './hooks/useCalendarState';
import { UNAVAILABILITY_CONFIG, PICKER_MINUTES } from './calendarConstants';
import { format, setTimeOfDay, workScheduleHoursForDate } from './hooks/dateUtils';

function UnavailabilityDialog({ t }) {
  const {
    unavailabilityDialogOpen,
    closeUnavailabilityDialog,
    selectedTimeSlot,
    employees,
    editingUnavailability,
    workSchedule,
    addUnavailability,
    updateUnavailability,
    removeUnavailability,
  } = useCalendarState();

  const isEditMode = !!editingUnavailability;

  // Parse work schedule hours for defaults
  // Default span without a clicked slot: today's work day (the week's bounds on a closed day)
  const { startHour: startHourInt, endHour: endHourInt } = workScheduleHoursForDate(workSchedule, new Date());

  const [formData, setFormData] = useState({
    employeeId: '',
    type: 'vacation',
    title: '',
    startDateTime: null,
    endDateTime: null,
  });
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Initialize form when dialog opens
  useEffect(() => {
    if (unavailabilityDialogOpen) {
      let initialData;

      if (isEditMode && editingUnavailability) {
        // Edit mode - parse existing unavailability data
        initialData = {
          employeeId: editingUnavailability.employee_id,
          type: editingUnavailability.type || 'vacation',
          title: editingUnavailability.title || '',
          startDateTime: new Date(editingUnavailability.start_time),
          endDateTime: new Date(editingUnavailability.end_time),
        };
      } else {
        // Create mode - use selected time slot from calendar
        let startDateTime, endDateTime;

        if (selectedTimeSlot) {
          const startDate = selectedTimeSlot.startDate || selectedTimeSlot.date;
          const endDate = selectedTimeSlot.endDate || selectedTimeSlot.date;

          // Build start datetime
          startDateTime = new Date(startDate);
          startDateTime.setHours(Math.floor(selectedTimeSlot.startHour), Math.round((selectedTimeSlot.startHour % 1) * 60), 0, 0);

          // Build end datetime
          endDateTime = new Date(endDate);
          endDateTime.setHours(Math.floor(selectedTimeSlot.endHour), Math.round((selectedTimeSlot.endHour % 1) * 60), 0, 0);
        } else {
          // Default: today, work hours
          startDateTime = new Date();
          setTimeOfDay(startDateTime, startHourInt);
          endDateTime = new Date(startDateTime);
          setTimeOfDay(endDateTime, endHourInt);
        }

        initialData = {
          employeeId: selectedTimeSlot?.resourceId || '',
          type: 'vacation',
          title: '',
          startDateTime,
          endDateTime,
        };
      }

      setFormData(initialData);
      setDeleteConfirm(false);
      setSaveError(null);
    }
  }, [unavailabilityDialogOpen, selectedTimeSlot, isEditMode, editingUnavailability, startHourInt, endHourInt]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // Ensure end time is always after start time
      if (field === 'startDateTime' && value && newData.endDateTime) {
        const minEnd = new Date(value.getTime() + PICKER_MINUTES * 60 * 1000);
        if (newData.endDateTime <= value) {
          newData.endDateTime = minEnd;
        }
      }
      if (field === 'endDateTime' && value && newData.startDateTime) {
        const minEnd = new Date(newData.startDateTime.getTime() + PICKER_MINUTES * 60 * 1000);
        if (value < minEnd) {
          newData.endDateTime = minEnd;
        }
      }

      return newData;
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setSaveError(null);

    try {
      const startDateTime = formData.startDateTime;
      const endDateTime = formData.endDateTime;

      const payload = {
        employee: formData.employeeId,
        type: formData.type,
        title: formData.title,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
      };

      if (isEditMode) {
        // Update existing unavailability
        const response = await axios.patch(
          `/api/scheduling/unavailabilities/${editingUnavailability.id}/`,
          payload
        );

        // Update local state with local_* fields for multi-day rendering
        updateUnavailability(editingUnavailability.id, {
          ...response.data,
          employee_id: response.data.employee_id || response.data.employee || formData.employeeId,
          local_start_date: format(startDateTime, 'yyyy-MM-dd'),
          local_end_date: format(endDateTime, 'yyyy-MM-dd'),
          local_start_hour: startDateTime.getHours() + startDateTime.getMinutes() / 60,
          local_end_hour: endDateTime.getHours() + endDateTime.getMinutes() / 60,
        });
      } else {
        // Create new unavailability
        const response = await axios.post('/api/scheduling/unavailabilities/', payload);

        // Add to local state with local_* fields for multi-day rendering
        addUnavailability({
          ...response.data,
          employee_id: response.data.employee_id || response.data.employee || formData.employeeId,
          local_start_date: format(startDateTime, 'yyyy-MM-dd'),
          local_end_date: format(endDateTime, 'yyyy-MM-dd'),
          local_start_hour: startDateTime.getHours() + startDateTime.getMinutes() / 60,
          local_end_hour: endDateTime.getHours() + endDateTime.getMinutes() / 60,
        });
      }

      closeUnavailabilityDialog();
    } catch (error) {
      console.error('Error saving unavailability:', error);
      const message = error.response?.data?.detail || error.response?.data?.message || t('Failed to save. Please try again.');
      setSaveError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    setLoading(true);
    setSaveError(null);
    try {
      await axios.delete(`/api/scheduling/unavailabilities/${editingUnavailability.id}/`);
      removeUnavailability(editingUnavailability.id);
      closeUnavailabilityDialog();
    } catch (error) {
      console.error('Error deleting unavailability:', error);
      const message = error.response?.data?.detail || error.response?.data?.message || t('Failed to delete. Please try again.');
      setSaveError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={unavailabilityDialogOpen}
      onClose={closeUnavailabilityDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {isEditMode ? t('Edit absence') : t('Mark as unavailable')}
        </Typography>
        <IconButton onClick={closeUnavailabilityDialog} sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Save Error */}
        {saveError && (
          <Alert
            severity="error"
            onClose={() => setSaveError(null)}
            sx={{ mb: 2.5, borderRadius: '8px' }}
          >
            {saveError}
          </Alert>
        )}

        {/* Employee Select / Display */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('Employee')}</Typography>
          {isEditMode ? (
            // In edit mode, show employee info but don't allow changing
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'grey.50',
                borderRadius: '10px',
                border: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {(() => {
                const emp = employees.find((e) => e.id === formData.employeeId);
                if (!emp) return null;
                return (
                  <>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: emp.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 600,
                      }}
                    >
                      {emp.initials}
                    </Box>
                    <Typography sx={{ fontSize: '14px' }}>{emp.name}</Typography>
                  </>
                );
              })()}
            </Box>
          ) : (
            <FormControl fullWidth size="small">
              <Select
                value={formData.employeeId}
                onChange={(e) => handleFieldChange('employeeId', e.target.value)}
                displayEmpty
                sx={{ borderRadius: '10px' }}
              >
                <MenuItem value="" disabled>
                  <Typography color="text.secondary">{t('Select employee...')}</Typography>
                </MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: emp.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: 600,
                        }}
                      >
                        {emp.initials}
                      </Box>
                      {emp.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Type Select */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('Type')}</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={formData.type}
              onChange={(e) => handleFieldChange('type', e.target.value)}
              sx={{ borderRadius: '10px' }}
            >
              {Object.entries(UNAVAILABILITY_CONFIG).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: config.color,
                      }}
                    />
                    {t(config.labelKey)}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Title */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>
            {t('Title')} ({t('optional')})
          </Typography>
          <TextField
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            placeholder={t('e.g. Winter vacation')}
            fullWidth
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
        </Box>

        {/* Start and End DateTime Row */}
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
          {/* Side by side on a desktop, stacked on a phone where two pickers would not fit */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('Start time')}</Typography>
              <DateTimePicker
                value={formData.startDateTime}
                onChange={(newValue) => handleFieldChange('startDateTime', newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } },
                  },
                }}
                ampm={false}
                minutesStep={PICKER_MINUTES}
                format="dd.MM.yyyy HH:mm"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('End time')}</Typography>
              <DateTimePicker
                value={formData.endDateTime}
                onChange={(newValue) => handleFieldChange('endDateTime', newValue)}
                minDateTime={formData.startDateTime ? new Date(formData.startDateTime.getTime() + PICKER_MINUTES * 60 * 1000) : undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } },
                  },
                }}
                ampm={false}
                minutesStep={PICKER_MINUTES}
                format="dd.MM.yyyy HH:mm"
              />
            </Box>
          </Box>
        </LocalizationProvider>

      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'space-between',
          // A phone's width: the buttons drop to a second row rather than break their labels
          flexWrap: 'wrap',
          rowGap: 1,
        }}
      >
        {/* Delete button - only in edit mode */}
        {isEditMode ? (
          <Button
            onClick={handleDelete}
            variant="outlined"
            color="error"
            disabled={loading}
            startIcon={<DeleteIcon />}
            sx={{
              borderRadius: '10px',
              fontWeight: 500,
              textTransform: 'none',
              px: 2,
            }}
          >
            {deleteConfirm ? <span>{t('Confirm delete')}</span> : <span>{t('Delete')}</span>}
          </Button>
        ) : (
          <Box />
        )}

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            onClick={closeUnavailabilityDialog}
            variant="outlined"
            sx={{
              borderRadius: '10px',
              borderColor: 'divider',
              color: 'text.primary',
              fontWeight: 500,
              textTransform: 'none',
              px: 2.5,
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.employeeId || !formData.startDateTime || !formData.endDateTime || loading}
            sx={{
              borderRadius: '10px',
              bgcolor: 'secondary.main',
              fontWeight: 500,
              textTransform: 'none',
              px: 2.5,
              '&:hover': { bgcolor: 'secondary.light' },
            }}
          >
            {t('Save')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(UnavailabilityDialog);
